import type { FrameworkConfig, ProjectConfig } from "../../types/types.js"
import { BaseFramework } from "../base-framework.js"

const vueConfig: FrameworkConfig = {
  defaultMeta: "none",
  displayName: "Vue",
  metaFrameworks: ["nuxt", "none"],
  name: "vue",
  starter: {
    args: (config: ProjectConfig) => {
      const args: Array<string> = []
      if (config.typescript) {
        args.push("--typescript")
      }
      return args
    },
    command: "create-vue",
  },
}

export class VueFramework extends BaseFramework {
  constructor() {
    super(vueConfig)
  }

  getStarterCommand(projectConfig: ProjectConfig): string {
    const { metaFramework } = projectConfig

    switch (metaFramework) {
      case "nuxt":
        return this.getNuxtCommand(projectConfig)
      case "none":
        return this.getViteVueCommand(projectConfig)
      default:
        return this.getViteVueCommand(projectConfig)
    }
  }

  private getNuxtCommand(projectConfig: ProjectConfig): string {
    const { projectName, typescript } = projectConfig
    const args = [
      projectName,
      "--no-install",
      "--package-manager",
      projectConfig.packageManager,
    ]

    // Nuxt 3 uses TypeScript by default, add --javascript flag if not using TS
    if (!typescript) {
      args.push("--javascript")
    }

    return `npx nuxi@latest init ${args.join(" ")}`
  }

  private getViteVueCommand(projectConfig: ProjectConfig): string {
    const { projectName, typescript } = projectConfig

    // Use create-vue for better Vue 3 setup
    const args = [
      projectName,
      "--ts",
      typescript ? "true" : "false",
      "--jsx",
      "false",
      "--router",
      "true",
      "--pinia",
      "true",
      "--vitest",
      "false",
      "--e2e",
      "false",
      "--eslint",
      "false",
      "--prettier",
      "false",
    ]

    return `npm create vue@latest ${args.join(" ")} -- --force`
  }

  getPostInstallTasks(
    projectConfig: ProjectConfig,
  ): Array<() => Promise<void>> {
    const tasks: Array<() => Promise<void>> = []

    // Add Vue-specific post-install tasks
    if (projectConfig.metaFramework === "nuxt") {
      tasks.push(async () => {
        // Nuxt-specific setup
        await this.updatePackageJson(projectConfig, {
          scripts: {
            build: "nuxt build",
            dev: "nuxt dev",
            generate: "nuxt generate",
            preview: "nuxt preview",
          },
        })
      })
    }

    return tasks
  }
}
